import { useMemo } from "react";
import type { ReactNode } from "react";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { UNLOCKS } from "plg/screens/Unlocks/unlocks";
import type { UnlockKey } from "plg/screens/Unlocks/unlocks";
import PlgIcon from "plg/shell/PlgIcon";
import { HIRE_ROUTES, hireUnlock } from "plg/routes";
import { PLG_FEATURES } from "plg/features";

export interface SidebarItem {
  id: string;
  title: string;
  path: string;
  icon: ReactNode;
  category: string;
  badgeText?: string;
  locked?: boolean;
  divider?: boolean;
  /** External links (Help) open in a new tab, as in the portal sidebar. */
  openInNewTab?: boolean;
  /** A small outlined tag after the title ("Coming soon"). */
  tagText?: string;
}

// Prototype NAV (index_last_day.html): Home; Hire (Browse, Shortlisted); Manage, the pages a customer gets once they
// hire (Team, Payroll, Time-off, Holidays, Performance); Setup (Billing, their account with Edge, not their team).
// Request a Hire and Interview status are not in the sidebar (Request a meeting is the one CTA; PLG interviews are off).
const UNLOCK_GROUPS: [string, UnlockKey[]][] = [
  // Performance is hidden for now (product 2026-11-04); its page still exists at /hire/unlocks/performance.
  ["Manage", ["team", "payroll", "timeoff", "holidays"]],
  ["Setup", ["billing"]],
];

export default function usePlgMenuItems(): SidebarItem[] {
  const { count } = useShortlist();
  return useMemo(
    () => [
      { id: "home", title: "Home", path: HIRE_ROUTES.home, icon: <PlgIcon name="home" />, category: "default" },
      ...(PLG_FEATURES.companyPage
        ? [{ id: "company", title: "Company", path: HIRE_ROUTES.company, icon: <PlgIcon name="building" />, category: "default" }]
        : []),
      {
        id: "talent-network",
        title: "Browse",
        path: HIRE_ROUTES.talentNetwork,
        icon: <PlgIcon name="globe" />,
        category: "Hire",
      },
      {
        id: "shortlist",
        title: "Shortlisted",
        path: HIRE_ROUTES.shortlist,
        icon: <PlgIcon name="bookmark" />,
        category: "Hire",
        // A plain count, nothing when the shortlist is empty (prototype .sb-count)
        ...(count ? { badgeText: `${count}` } : {}),
      },
      ...UNLOCK_GROUPS.flatMap(([category, keys]) =>
        keys.map((key) => ({
          id: `unlock-${key}`,
          title: UNLOCKS[key].label,
          path: hireUnlock(key),
          icon: <PlgIcon name={UNLOCKS[key].icon} />,
          category,
          ...(UNLOCKS[key].soon ? { tagText: "Coming soon" } : {}),
        }))
      ),
    ],
    [count]
  );
}

/** Prototype FOOT: inert items under the menu (no destination yet). */
export function plgFooterItems(): SidebarItem[] {
  return [
    // No Help link for now (product 2026-10-29).
    // The portal's Feedback screen, under /hire (it sends as the PLG customer).
    { id: "feedback", title: "Feedback", path: HIRE_ROUTES.feedback, icon: <PlgIcon name="feedback" />, category: "footer" },
  ];
}
